/**
 * WorkOrderCreateModal Component
 *
 * Multi-step wizard for creating new work orders.
 * Notes stay in the left panel on desktop, and behind a button on mobile.
 * The AI service-history summary has its own right panel on every step.
 * It is requested as soon as an existing vehicle is selected, and again
 * when that vehicle is replaced. On mobile it stays above the steps.
 * Desktop dialog size and both panel widths are restored from localStorage.
 *
 * Step flow (4 steps):
 *   CustomerVehicle → Services → Scheduling → Summary
 *
 * Features:
 * - Combined customer and vehicle lookup in first step
 * - Inline new-vehicle and new-customer forms within the first step
 * - External vehicle data lookup when local search fails
 * - Inline new customer creation with business/individual types
 * - Contact person selection for business customers
 * - Pre-filling of vehicle data from external sources
 */

import { useState, useMemo, useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepButton,
  StepContent,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { VehicleHistorySummary } from './create/VehicleHistorySummary';
import {
  DIALOG_KEYBOARD_STEP,
  SIDE_PANEL_KEYBOARD_STEP,
  sidePanelBounds,
  trackPointerResize,
  useWorkOrderCreateLayout,
  type WorkOrderCreateLayout,
} from './create/workOrderCreateLayout';
import {
  WorkOrderCreateDialogResize,
  WorkOrderCreateSidePanelResize,
} from './create/WorkOrderCreateLayoutControls';
import { useIsMobile } from '@/hooks/useIsMobile';
import { format, addDays, isSameDay, parseISO, startOfDay } from 'date-fns';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useCreateVehicle } from '@/hooks/useVehicles';
import { useCreateBooking, type CreateBookingVariables } from '@/hooks/useBookings';
import type { WorkOrderPartyRole } from '@/lib/workOrderPartyRoles';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useAllLifts } from '@/hooks/useLifts';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { sumHours } from '@/utils/duration';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { QuoteAcceptFlow } from './QuoteAcceptFlow';
import type { BookingListItem } from '@/hooks/useBookings';
import {
  initialVehicleFieldsData,
  type VehicleFieldsData,
} from '@/components/vehicles/VehicleFormFields';
import type { VehicleDetail, VehicleListItem } from '@/hooks/useVehicles';
import { useCreatePerson } from '@/hooks/usePersons';
import {
  lookupCompaniesByBusinessId,
  useCreateCompany,
  type BusinessIdMatch,
} from '@/hooks/useCompanies';
import { BusinessIdConflictDialog } from '@/components/companies/BusinessIdConflictDialog';
import { ApiError, api } from '@/lib/api';
import {
  initialPersonFieldsData,
  type PersonFieldsData,
} from '@/components/persons/PersonFormFields';
import {
  initialCompanyFieldsData,
  type CompanyFieldsData,
} from '@/components/companies/CompanyFormFields';
import { CustomerVehicleStep, ordererPartyFromVehicle, type PartyRef, type InlinePartyCreate } from './create/CustomerVehicleStep';
import {
  commitCustomerVehicleStep,
  validateCustomerVehicleDraft,
  type CompanyBusinessIdChoice,
} from './create/commitCustomerVehicleStep';
import { SchedulingStep } from './create/SchedulingStep';
import { suggestedPickupAt } from './create/pickupEstimate';
import {
  isPickupBeforeScheduledRefusal,
  pickupBeforeScheduled,
  requestScheduleTimes,
} from './create/requestTimes';
import { suggestedDropoffAt } from './create/dropoffDefault';
import { CourtesyVehicleSelect } from './create/CourtesyVehicleSelect';
import { reserveCourtesyAfterCreate, type CreateCourtesySelection } from './create/courtesyAfterCreate';
import { defaultCourtesyLoanRange, useReserveCourtesyLoan } from '@/hooks/useCourtesyVehicles';
import {
  ServiceStep,
  toCreateEstimatedDurationHours,
  toCreateServiceDurations,
  toCreateServiceLaborItemIds,
  type SelectedServiceData,
} from './create/ServiceStep';
import { SummaryStep, type CreateConfirmationMethod } from './create/SummaryStep';
import type { CreateChecklistSelection } from './create/ChecklistTemplateSelect';
import { WorkOrderNotesDialog } from './WorkOrderNotesDialog';
import { WorkOrderNotesPreview } from './WorkOrderNotesPreview';
import NotesIcon from '@mui/icons-material/Notes';
import type { NoteCategory, SchedulingType, WorkshopOpeningHours } from '@/types';
import { openTimeForDate, openingHoursForDate } from '@/utils/openingHours';
import { useFormDraft, type StoredDraft } from '@/hooks/useFormDraft';
import { describeApiError } from '@/lib/describeApiError';
import { eurosToCents } from '@/utils/money';
import { emitGuideEvent, guideAnchor, GUIDE_DIALOG_CLOSED_EVENT } from '@/lib/guides/engine';

// --- Step identifiers ---

type StepId = 'customerVehicle' | 'scheduling' | 'services' | 'summary';

// --- Helpers ---

export function toDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}


/** Form value for a floating date field (`datetime-local` date + dummy midnight). */
export function floatingFormDateTime(civilDate: string): string {
  const day = civilDate.split('T')[0] ?? civilDate;
  return `${day}T00:00`;
}

/**
 * Civil date to show for a stored floating `scheduledAt`. Canonical UTC-midnight
 * rows use the UTC date (the date the UI wrote). Legacy local-midnight rows use
 * the browser zone so a Helsinki save still displays the promised day.
 */
export function floatingCivilDateFromStored(iso: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) {
    return iso.split('T')[0] ?? iso;
  }
  if (instant.getUTCHours() === 0 && instant.getUTCMinutes() === 0) {
    return instant.toISOString().slice(0, 10);
  }
  return toDateTimeLocal(instant).split('T')[0] ?? instant.toISOString().slice(0, 10);
}

function guideCustomerKind(
  orderer: PartyRef | null,
  inline: InlinePartyCreate,
): 'person' | 'company' {
  if (inline === 'company' || orderer?.partyType === 'company') return 'company';
  return 'person';
}

function atMinutesOfDay(day: Date, minutesOfDay: number): Date {
  const date = new Date(day);
  date.setHours(Math.floor(minutesOfDay / 60), minutesOfDay % 60, 0, 0);
  return date;
}

/**
 * Next 15-min slot inside the workshop's opening hours, else the next open
 * day's opening time (#4095). Without saved hours (or with no open day in the
 * coming week) the long-standing 08–17 window applies: the next slot today,
 * else 08:00 the next day.
 */
export function getDefaultScheduledAt(
  businessHours?: WorkshopOpeningHours | null,
  now: Date = new Date(),
): string {
  const slot = new Date(now);
  slot.setSeconds(0, 0);
  slot.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
  const today = startOfDay(now);
  const slotToday = isSameDay(slot, today);
  const slotMinutes = slot.getHours() * 60 + slot.getMinutes();

  if (businessHours) {
    for (let offset = 0; offset < 7; offset += 1) {
      const day = addDays(today, offset);
      const hours = openingHoursForDate(day, businessHours);
      if (!hours) continue;
      if (offset > 0) return toDateTimeLocal(atMinutesOfDay(day, hours.openMinutes));
      if (!slotToday || slotMinutes >= hours.closeMinutes) continue;
      if (slotMinutes < hours.openMinutes) {
        return toDateTimeLocal(atMinutesOfDay(day, hours.openMinutes));
      }
      return toDateTimeLocal(slot);
    }
  }

  if (slotToday && slot.getHours() >= 8 && slot.getHours() < 17) return toDateTimeLocal(slot);
  return toDateTimeLocal(atMinutesOfDay(addDays(today, 1), 8 * 60));
}

/** Footer button visibility / disable / loading for the create wizard (#2112). */
export function getCreateWizardFooterButtons(args: {
  activeStepIndex: number;
  stepCount: number;
  canProceed: boolean;
  isSavingCustomerVehicle: boolean;
  isSubmitting: boolean;
}): {
  showPrevious: boolean;
  primaryAction: 'next' | 'submit';
  primaryDisabled: boolean;
  primaryLoading: boolean;
} {
  const isLast = args.activeStepIndex === args.stepCount - 1;
  const showPrevious = args.activeStepIndex > 0;
  if (isLast) {
    return {
      showPrevious,
      primaryAction: 'submit',
      primaryDisabled: args.isSubmitting,
      primaryLoading: args.isSubmitting,
    };
  }
  return {
    showPrevious,
    primaryAction: 'next',
    primaryDisabled: !args.canProceed || args.isSavingCustomerVehicle,
    primaryLoading: args.isSavingCustomerVehicle,
  };
}

/**
 * What clicking a step header does (#3944). Earlier steps and steps already
 * reached jump straight there; the step right after the active one acts as the
 * Next button — same handler, greyed out exactly when Next is.
 */
export function getCreateWizardStepHeader(args: {
  index: number;
  activeStepIndex: number;
  maxStepIndex: number;
  nextDisabled: boolean;
}): { action: 'jump' | 'next' | 'none'; disabled: boolean } {
  if (args.index === args.activeStepIndex + 1) {
    return { action: 'next', disabled: args.nextDisabled };
  }
  if (args.index <= args.maxStepIndex) {
    return { action: 'jump', disabled: false };
  }
  return { action: 'none', disabled: true };
}

/** The create request body, as the contract types it (#3160). */
type WizardBooking = CreateBookingVariables['body'];

/** One submit attempt: its key, what it sent, and whether the server proved it wrote nothing (#3160). */
interface SubmitAttempt {
  key: string;
  fingerprint: string;
  refusedBeforeWrite: boolean;
}

/** Body field of a staff-api error response, when there is one. */
function responseField(error: ApiError, field: string): unknown {
  const data = error.data;
  return typeof data === 'object' && data !== null ? (data as Record<string, unknown>)[field] : undefined;
}

/**
 * True only when the server answered in a way that proves the create wrote
 * nothing: a refusal before any write (400/401/403/404), or the lift clash
 * (409 lift_unavailable). A lost response, a 5xx, or a 409/422 about a key
 * (in progress, outcome unknown, reused with another body) is not proof.
 */
function refusedBeforeWrite(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if ([400, 401, 403, 404].includes(error.status)) return true;
  return error.status === 409 && responseField(error, 'code') === 'lift_unavailable';
}

/** The previous attempt may have written: the server, not the wizard, decides what a retry does. */
function outcomeMayBeUnknown(error: unknown): boolean {
  return error instanceof ApiError && !refusedBeforeWrite(error) && (error.status === 409 || error.status === 422);
}

/**
 * The Idempotency-Key for this submit (#3160). A button press or a lost
 * response under the same body reuses the key, so the server replays the first
 * outcome. A new key is minted only when the previous attempt was refused
 * before any write and the body changed. An attempt that may have written keeps
 * its key whatever the body, so a second work order is never created blind.
 */
function nextSubmitKey(previous: SubmitAttempt | null, fingerprint: string): string {
  if (previous === null) return crypto.randomUUID();
  if (!previous.refusedBeforeWrite || previous.fingerprint === fingerprint) return previous.key;
  return crypto.randomUUID();
}

// --- Component ---

/** What the create dialog keeps in its localStorage draft (#3404). */
interface CreateWorkOrderDraft {
  resolvedVehicle: VehicleListItem | null;
  isCreatingNewVehicle: boolean;
  newVehicleData: VehicleFieldsData;
  ordererParty: PartyRef | null;
  ordererSource: 'vehicle' | 'manual' | null;
  contactPersonRegistryId: string;
  additionalParties: Partial<Record<WorkOrderPartyRole, PartyRef>>;
  inlinePartyCreate: InlinePartyCreate;
  personDraft: PersonFieldsData;
  companyDraft: CompanyFieldsData;
  mileageInput: string;
  preapprovedLimitInput: string;
  quoteValidityDaysInput: string;
  quoteValidityTouched: boolean;
  customerWaits: boolean;
  allowMinorMaintenance: boolean;
  jointInspectionRequested: boolean;
  confirmationMethod: CreateConfirmationMethod | null;
  selectedChecklists: CreateChecklistSelection[];
  additionalQuestionAnswers: Record<string, string>;
  schedulingType: SchedulingType;
  schedulingTypeTouched: boolean;
  scheduledAtStr: string;
  vehicleDropoffAtStr: string;
  vehicleDropoffAtTouched: boolean;
  pickupAtStr: string;
  pickupAtTouched: boolean;
  durationHours: number;
  durationTouched: boolean;
  serviceBookChecked: boolean;
  selectedServices: SelectedServiceData[];
  customerNotesText: string;
  localNotes: { category: NoteCategory; content: string; authorName: string; createdAt: string }[];
  /** Courtesy car reserved right after the work order is saved (#4103). */
  courtesyVehicle: CreateCourtesySelection | null;
}

/** Defaults for any field an older or partial draft lacks. */
function emptyCreateWorkOrderDraft(): CreateWorkOrderDraft {
  return {
    resolvedVehicle: null,
    isCreatingNewVehicle: false,
    newVehicleData: { ...initialVehicleFieldsData },
    ordererParty: null,
    ordererSource: null,
    contactPersonRegistryId: '',
    additionalParties: {},
    inlinePartyCreate: null,
    personDraft: { ...initialPersonFieldsData },
    companyDraft: { ...initialCompanyFieldsData },
    mileageInput: '',
    preapprovedLimitInput: '',
    quoteValidityDaysInput: '',
    quoteValidityTouched: false,
    customerWaits: false,
    allowMinorMaintenance: false,
    jointInspectionRequested: false,
    confirmationMethod: null,
    selectedChecklists: [],
    additionalQuestionAnswers: {},
    schedulingType: 'floating',
    schedulingTypeTouched: false,
    scheduledAtStr: '',
    vehicleDropoffAtStr: '',
    vehicleDropoffAtTouched: false,
    pickupAtStr: '',
    pickupAtTouched: false,
    durationHours: 0,
    durationTouched: false,
    serviceBookChecked: false,
    selectedServices: [],
    customerNotesText: '',
    localNotes: [],
    courtesyVehicle: null,
  };
}

interface WorkOrderCreateModalProps {
  open: boolean;
  onClose: () => void;
  mechanics: Array<{ id: string; firstName: string; lastName: string; isAvailable: boolean }>;
  initialDate?: Date;
  initialMechanicId?: string;
  /** Prefill step 1 as if this existing vehicle was picked (#2899). */
  initialVehicle?: VehicleListItem;
  /** Entry-point flavour: 'workOrder' creates with status='pending', 'quote' creates a quotation (#560). */
  mode?: 'workOrder' | 'quote';
}

/**
 * The newest still-valid quote on a vehicle, or null (#2653). Read-only and
 * best-effort: a lookup failure must not stop the wizard, so errors are
 * swallowed and the wizard simply continues to create.
 */
async function findOpenQuoteForVehicle(vehicleId: string): Promise<BookingListItem | null> {
  try {
    const response = await api.get<{ data?: BookingListItem[] }>('/bookings', {
      params: { vehicleId, status: 'quote', sortBy: 'createdAt', sortOrder: 'desc', limit: 10 },
    });
    const now = Date.now();
    const open = (Array.isArray(response?.data) ? response.data : []).filter(
      (quote) =>
        quote.status === 'quote'
        && (!quote.quoteExpiresAt || new Date(quote.quoteExpiresAt).getTime() > now),
    );
    return open[0] ?? null;
  } catch {
    return null;
  }
}

export function WorkOrderCreateModal({
  open,
  onClose,
  initialDate,
  initialMechanicId,
  initialVehicle,
  mode = 'workOrder',
}: WorkOrderCreateModalProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { t: tVehicles } = useTranslation('vehicles');
  const { t: tPersons } = useTranslation('persons');
  const { t: tCompanies } = useTranslation('companies');
  const notify = useNotify();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const endCreateResize = useRef<(() => void) | null>(null);
  useEffect(() => () => { endCreateResize.current?.(); }, []);
  const showManagerNotes = useAuthStore((s) => s.hasRole(['manager', 'owner', 'admin']));

  // Mutations
  const createBooking = useCreateBooking();
  const createVehicleMutation = useCreateVehicle();
  const createPersonMutation = useCreatePerson();
  const createCompanyMutation = useCreateCompany();
  const reserveCourtesyLoan = useReserveCourtesyLoan();

  // Mechanics (for summary step)
  const { data: mechanics = [] } = useMechanics();

  // Lifts — for naming the chosen lift in the summary (#1993)
  const { data: allLifts = [] } = useAllLifts();

  // Stepper
  const [activeStep, setActiveStep] = useState(0);
  // Furthest step reached — keeps completed sections clickable for editing even
  // after jumping back in the progressive inline flow (#561).
  const [maxStep, setMaxStep] = useState(0);

  // Step 1: unified customer/vehicle search input (#1002)
  const [searchInput, setSearchInput] = useState('');
  const [resolvedVehicle, setResolvedVehicle] = useState<VehicleListItem | null>(null);
  const [isCreatingNewVehicle, setIsCreatingNewVehicle] = useState(false);

  // A drafted vehicle has no id until step 1 saves it. Changing
  // resolvedVehicle.id starts a new request; the summary stays mounted
  // across steps, including quotations.
  const historyVehicleId = !isCreatingNewVehicle ? resolvedVehicle?.id : undefined;
  const showHistoryPanel = !isMobile && historyVehicleId !== undefined;
  const { layout: createLayout, storedLayout, updateLayout } = useWorkOrderCreateLayout(showHistoryPanel);
  const createLayoutRef = useRef(createLayout);
  const storedLayoutRef = useRef(storedLayout);
  createLayoutRef.current = createLayout;
  storedLayoutRef.current = storedLayout;

  // New vehicle state — the registration number is prefilled by the unified
  // search's create-vehicle action (registry lookup, #562/#1002).
  const [newVehicleData, setNewVehicleData] = useState<VehicleFieldsData>({ ...initialVehicleFieldsData });
  const [newVehicleErrors, setNewVehicleErrors] = useState<Partial<Record<keyof VehicleFieldsData, string>>>({});

  // Orderer party state (#833 B1): the work order's orderer is a person/company
  // from the registries — resolved from the vehicle's owner link or picked manually.
  const [ordererParty, setOrdererParty] = useState<PartyRef | null>(null);
  const [ordererSource, setOrdererSource] = useState<'vehicle' | 'manual' | null>(null);
  const [contactPersonRegistryId, setContactPersonRegistryId] = useState('');

  // Additional (optional) work order party roles (#1108) — e.g. insurance_company,
  // leasing_company, pickup_person. Held locally and persisted after the work
  // order is created, via the existing per-role parties endpoint.
  const [additionalParties, setAdditionalParties] = useState<Partial<Record<WorkOrderPartyRole, PartyRef>>>({});

  const handleAddParty = useCallback((role: WorkOrderPartyRole, party: PartyRef) => {
    setAdditionalParties((prev) => ({ ...prev, [role]: party }));
  }, []);
  const handleRemoveParty = useCallback((role: WorkOrderPartyRole) => {
    setAdditionalParties((prev) => {
      const next = { ...prev };
      delete next[role];
      return next;
    });
  }, []);

  // Inline person/company creation drafts (#1002) — lifted here so they survive
  // the step collapsing (StepContent unmounts on exit). Their validation errors
  // live here too because the step-1 Next button validates and saves the
  // drafts (#1020); there is no separate save button in the wizard.
  const [inlinePartyCreate, setInlinePartyCreate] = useState<InlinePartyCreate>(null);
  const [personDraft, setPersonDraft] = useState<PersonFieldsData>({ ...initialPersonFieldsData });
  const [personDraftErrors, setPersonDraftErrors] = useState<Partial<Record<keyof PersonFieldsData, string>>>({});
  const [companyDraft, setCompanyDraft] = useState<CompanyFieldsData>({ ...initialCompanyFieldsData });
  const [companyDraftErrors, setCompanyDraftErrors] = useState<Partial<Record<keyof CompanyFieldsData, string>>>({});
  const [companyConflict, setCompanyConflict] = useState<BusinessIdMatch[] | null>(null);
  const [isCommittingStep, setIsCommittingStep] = useState(false);
  const conflictChoiceRef = useRef<((choice: CompanyBusinessIdChoice) => void) | null>(null);
  // The submit in flight or the last ambiguous one, so a retry keeps its key (#3160).
  const submitAttemptRef = useRef<SubmitAttempt | null>(null);
  const committingRef = useRef(false);
  // Generation of this open. Close, unmount, and a parent setting open=false
  // bump it and cancel a pending business-id choice, so a lookup that returns
  // later cannot open the warning or advance the step on the next open (#2410).
  const wizardSessionRef = useRef(0);
  const mountedRef = useRef(true);
  const openRef = useRef(open);
  openRef.current = open;

  const {
    tenantSettings,
    defaultQuoteValidityDays,
    defaultSchedulingType,
    defaultPickupBufferMinutes,
    workDurationStepMinutes,
    additionalQuestions = [],
  } = useTenantSettings();
  // Saved opening hours only: undefined keeps the 08–17 fallback (#4095).
  const workshopHours = tenantSettings?.businessHours;

  // Scheduling state
  const [scheduledAtStr, setScheduledAtStr] = useState(() =>
    initialDate ? toDateTimeLocal(initialDate) : getDefaultScheduledAt(workshopHours),
  );
  // Exact time vs during-the-day (#1138). The default is the workshop's
  // setting, not a hardcoded 'exact' (#1931): seeded by the effect below once
  // /tenants/me has answered, and frozen the moment the user picks a type.
  const [schedulingType, setSchedulingType] = useState<SchedulingType>('floating');
  const [schedulingTypeTouched, setSchedulingTypeTouched] = useState(false);
  // Vehicle drop-off follows the booking until the user edits it (#4124).
  const [vehicleDropoffAtStr, setVehicleDropoffAtStr] = useState('');
  const [vehicleDropoffAtTouched, setVehicleDropoffAtTouched] = useState(false);

  // Customer-requested pickup timestamp (#529 — was date-only #271). Follows
  // the duration+buffer estimate until the user explicitly picks a value (#2117).
  const [pickupAtStr, setPickupAtStr] = useState<string>(() =>
    initialDate ? toDateTimeLocal(initialDate) : getDefaultScheduledAt(workshopHours),
  );
  const [pickupAtTouched, setPickupAtTouched] = useState(false);

  // The dialog stays mounted between opens (the calendar only flips `open`),
  // so the initialisers above run once. Re-seed arrival and pickup on every
  // open, and when the calendar hands over a different slot while open, so
  // the schedule step shows the clicked slot and not today or the previous
  // slot (#4091, regression of #55). Done during render, not in an effect,
  // so the guide's schedule baseline already sees the seeded value.
  const initialDateKey = initialDate ? initialDate.getTime() : null;
  const [scheduleSeed, setScheduleSeed] = useState<{ open: boolean; dateKey: number | null }>(
    { open, dateKey: initialDateKey },
  );
  if (scheduleSeed.open !== open || (open && scheduleSeed.dateKey !== initialDateKey)) {
    const opening = open && !scheduleSeed.open;
    setScheduleSeed({ open, dateKey: initialDateKey });
    if (opening || (open && initialDate)) {
      const seeded = initialDate ? toDateTimeLocal(initialDate) : getDefaultScheduledAt(workshopHours);
      setScheduledAtStr(seeded);
      // A pickup the user picked by hand wins over a slot change mid-dialog (#2117).
      if (opening || !pickupAtTouched) setPickupAtStr(seeded);
    }
  }

  // Services state (replaces lineItems)
  const [selectedServices, setSelectedServices] = useState<SelectedServiceData[]>([]);

  // Customer waits state
  const [customerWaits, setCustomerWaits] = useState(false);

  // Allow minor maintenance state
  const [allowMinorMaintenance, setAllowMinorMaintenance] = useState(false);

  // Joint inspection requested state
  const [jointInspectionRequested, setJointInspectionRequested] = useState(false);

  // How the booking was confirmed with the customer (#2126). `null` = leave
  // unconfirmed; a method is sent as `confirmationMethod` on create.
  const [confirmationMethod, setConfirmationMethod] = useState<CreateConfirmationMethod | null>(null);

  // Optional inspection / service-book lists to attach after create (#2128).
  const [selectedChecklists, setSelectedChecklists] = useState<CreateChecklistSelection[]>([]);

  // Reception-question answers keyed by question id (#2130). Empty/whitespace
  // answers are omitted from the create payload.
  const [additionalQuestionAnswers, setAdditionalQuestionAnswers] = useState<Record<string, string>>({});

  // Mileage state
  const [mileageInput, setMileageInput] = useState('');

  // Pre-approved additional work limit state (#66)
  const [preapprovedLimitInput, setPreapprovedLimitInput] = useState('');

  // Quote validity in days (#849) — only used in quote mode. Seeded from the
  // workshop default and mirrors it until the user edits the field. The effect
  // lives below, after the workshop default is read from tenant settings.
  const [quoteValidityDaysInput, setQuoteValidityDaysInput] = useState('');
  const [quoteValidityTouched, setQuoteValidityTouched] = useState(false);

  // Autofill default for the limit (#296): when the user toggles
  // allowMinorMaintenance ON and the limit field is empty, fill from the
  // workshop default. (The legacy per-customer default lived on the customers
  // table and is retired with it, #833 B1; a per-party default can be added to
  // persons/companies later if needed.)
  const workshopDefaultLimitCents = tenantSettings?.defaultAdditionalWorkLimitCents ?? null;

  const servicesDurationHours = useMemo(
    () => sumHours(selectedServices.map((s) => s.estimatedDurationHours)),
    [selectedServices],
  );
  // No services: the calendar total follows the service sum until the user
  // edits it, and that edit is what create sends (#2336). With services the
  // server derives the total, so the manual value is neither shown nor sent
  // (#4123). It is kept so removing every service restores the edit.
  const [durationHours, setDurationHours] = useState(0);
  const [durationTouched, setDurationTouched] = useState(false);
  const calendarDurationHours =
    selectedServices.length > 0 ? servicesDurationHours : durationHours;
  // Service book is a booking column, but the booking does not exist while the
  // dialog is open — hold the reception's answer here and write it after create (#2339).
  const [serviceBookChecked, setServiceBookChecked] = useState(false);

  useEffect(() => {
    if (durationTouched) return;
    setDurationHours(servicesDurationHours);
  }, [durationTouched, servicesDurationHours]);

  // Seed the scheduling type from the workshop default until the user picks
  // one (#1931). "Customer waits" steers the default to exact, but does not
  // lock it — a manual choice (schedulingTypeTouched) always wins (#2115).
  useEffect(() => {
    if (schedulingTypeTouched) return;
    setSchedulingType(customerWaits ? 'exact' : defaultSchedulingType);
  }, [schedulingTypeTouched, defaultSchedulingType, customerWaits]);

  // Follow the pickup suggestion until the user edits the field (#2117).
  // Floating arrival is a midnight day-marker, so the estimate starts from
  // that day's opening time instead (#4095).
  const floatingOpenTime = openTimeForDate(scheduledAtStr, workshopHours);
  useEffect(() => {
    if (pickupAtTouched) return;
    const suggestion = suggestedPickupAt({
      schedulingType,
      arrivalAtStr: scheduledAtStr,
      durationHours: calendarDurationHours,
      bufferMinutes: defaultPickupBufferMinutes,
      openTime: floatingOpenTime,
    });
    if (suggestion != null && suggestion !== pickupAtStr) {
      setPickupAtStr(suggestion);
    }
  }, [
    scheduledAtStr,
    pickupAtTouched,
    pickupAtStr,
    calendarDurationHours,
    defaultPickupBufferMinutes,
    schedulingType,
    floatingOpenTime,
  ]);

  // Follow the booking with the drop-off until the user edits the field:
  // the appointment itself, or the day's opening time for floating work (#4124).
  useEffect(() => {
    if (vehicleDropoffAtTouched) return;
    const suggestion = suggestedDropoffAt({
      schedulingType,
      arrivalAtStr: scheduledAtStr,
      openTime: floatingOpenTime,
    });
    if (suggestion !== vehicleDropoffAtStr) setVehicleDropoffAtStr(suggestion);
  }, [scheduledAtStr, schedulingType, floatingOpenTime, vehicleDropoffAtTouched, vehicleDropoffAtStr]);

  // Seed the quote validity from the workshop default until the user edits it (#849).
  useEffect(() => {
    if (mode !== 'quote' || quoteValidityTouched) return;
    setQuoteValidityDaysInput(String(defaultQuoteValidityDays));
  }, [mode, quoteValidityTouched, defaultQuoteValidityDays]);

  useEffect(() => {
    if (!allowMinorMaintenance) return;
    if (preapprovedLimitInput.trim() !== '') return;
    if (workshopDefaultLimitCents != null && workshopDefaultLimitCents > 0) {
      setPreapprovedLimitInput((workshopDefaultLimitCents / 100).toFixed(2).replace('.', ','));
    }
  }, [allowMinorMaintenance, preapprovedLimitInput, workshopDefaultLimitCents]);

  // Customer notes — simple text field (not chat)
  const [customerNotesText, setCustomerNotesText] = useState('');

  // Optional courtesy car, reserved after the work order is saved (#4103).
  const [courtesyVehicle, setCourtesyVehicle] = useState<CreateCourtesySelection | null>(null);

  // Notes state — chat-style local notes before booking is created (internal/manager only)
  const [localNotes, setLocalNotes] = useState<{ category: NoteCategory; content: string; authorName: string; createdAt: string }[]>([]);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);

  // Local note add handler for creation mode
  const user = useAuthStore((s) => s.user);
  const handleLocalNoteAdd = useCallback((category: NoteCategory, content: string) => {
    const authorName = user ? `${user.firstName} ${user.lastName}` : '';
    setLocalNotes((prev) => [...prev, { category, content, authorName, createdAt: new Date().toISOString() }]);
    setIsDirty(true);
  }, [user]);

  // Dirty state
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Draft in localStorage (#3404): a crash or a closed tab must not lose what
  // was typed. Keyed per tenant and user (shared terminals), offered back on the
  // next open, cleared after a create or an explicit discard.
  const formDraft = useFormDraft<CreateWorkOrderDraft>(mode === 'quote' ? 'quote-create' : 'work-order-create');
  const [restorableDraft, setRestorableDraft] = useState<StoredDraft<CreateWorkOrderDraft> | null>(null);
  useEffect(() => {
    setRestorableDraft(open ? formDraft.load() : null);
  }, [open, formDraft]);
  const draftData = useMemo<CreateWorkOrderDraft>(() => ({
    resolvedVehicle, isCreatingNewVehicle, newVehicleData,
    ordererParty, ordererSource, contactPersonRegistryId, additionalParties,
    inlinePartyCreate, personDraft, companyDraft,
    mileageInput, preapprovedLimitInput, quoteValidityDaysInput, quoteValidityTouched,
    customerWaits, allowMinorMaintenance, jointInspectionRequested, confirmationMethod,
    selectedChecklists, additionalQuestionAnswers,
    schedulingType, schedulingTypeTouched, scheduledAtStr, vehicleDropoffAtStr, vehicleDropoffAtTouched,
    pickupAtStr, pickupAtTouched, durationHours, durationTouched,
    serviceBookChecked, selectedServices, customerNotesText, localNotes, courtesyVehicle,
  }), [
    resolvedVehicle, isCreatingNewVehicle, newVehicleData,
    ordererParty, ordererSource, contactPersonRegistryId, additionalParties,
    inlinePartyCreate, personDraft, companyDraft,
    mileageInput, preapprovedLimitInput, quoteValidityDaysInput, quoteValidityTouched,
    customerWaits, allowMinorMaintenance, jointInspectionRequested, confirmationMethod,
    selectedChecklists, additionalQuestionAnswers,
    schedulingType, schedulingTypeTouched, scheduledAtStr, vehicleDropoffAtStr, vehicleDropoffAtTouched,
    pickupAtStr, pickupAtTouched, durationHours, durationTouched,
    serviceBookChecked, selectedServices, customerNotesText, localNotes, courtesyVehicle,
  ]);
  useEffect(() => {
    if (!open || !isDirty) return;
    formDraft.save(draftData);
  }, [open, isDirty, draftData, formDraft]);
  // Offered only until the user starts a new one; typing then overwrites it.
  const showDraftBanner = restorableDraft !== null && !isDirty;
  const restoreDraft = useCallback(() => {
    if (!restorableDraft) return;
    const d = { ...emptyCreateWorkOrderDraft(), ...restorableDraft.data };
    setResolvedVehicle(d.resolvedVehicle);
    setIsCreatingNewVehicle(d.isCreatingNewVehicle);
    setNewVehicleData(d.newVehicleData);
    setOrdererParty(d.ordererParty);
    setOrdererSource(d.ordererSource);
    setContactPersonRegistryId(d.contactPersonRegistryId);
    setAdditionalParties(d.additionalParties);
    setInlinePartyCreate(d.inlinePartyCreate);
    setPersonDraft(d.personDraft);
    setCompanyDraft(d.companyDraft);
    setMileageInput(d.mileageInput);
    setPreapprovedLimitInput(d.preapprovedLimitInput);
    setQuoteValidityDaysInput(d.quoteValidityDaysInput);
    setQuoteValidityTouched(d.quoteValidityTouched);
    setCustomerWaits(d.customerWaits);
    setAllowMinorMaintenance(d.allowMinorMaintenance);
    setJointInspectionRequested(d.jointInspectionRequested);
    setConfirmationMethod(d.confirmationMethod);
    setSelectedChecklists(d.selectedChecklists);
    setAdditionalQuestionAnswers(d.additionalQuestionAnswers);
    setSchedulingType(d.schedulingType);
    setSchedulingTypeTouched(d.schedulingTypeTouched);
    // A slot clicked in the calendar is the newer intent: the draft's times
    // belong to another day, so keep the slot's arrival and estimate (#4091).
    if (!initialDate) {
      if (d.scheduledAtStr) setScheduledAtStr(d.scheduledAtStr);
      setVehicleDropoffAtStr(d.vehicleDropoffAtStr);
      // Drafts saved before #4124 lack the flag; a stored value was typed by hand.
      setVehicleDropoffAtTouched(
        (restorableDraft.data as Partial<CreateWorkOrderDraft>).vehicleDropoffAtTouched ?? d.vehicleDropoffAtStr !== '',
      );
      if (d.pickupAtStr) setPickupAtStr(d.pickupAtStr);
      setPickupAtTouched(d.pickupAtTouched);
    }
    setDurationHours(d.durationHours);
    setDurationTouched(d.durationTouched);
    setServiceBookChecked(d.serviceBookChecked);
    setSelectedServices(d.selectedServices);
    setCustomerNotesText(d.customerNotesText);
    setLocalNotes(d.localNotes);
    // The car is re-checked against availability when the schedule step shows.
    setCourtesyVehicle(d.courtesyVehicle);
    // The wizard starts over from the first step, so inline creates still
    // go through their own step commit.
    setActiveStep(0);
    setMaxStep(0);
    setRestorableDraft(null);
    setIsDirty(true);
  }, [restorableDraft, initialDate]);
  const discardDraft = useCallback(() => {
    formDraft.clear();
    setRestorableDraft(null);
  }, [formDraft]);
  // An open quote already on the chosen vehicle (#2653): offered before the
  // wizard goes on to create a second work order for the same job.
  const [openQuoteCandidate, setOpenQuoteCandidate] = useState<BookingListItem | null>(null);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | null>(null);

  // Parse scheduled date
  const scheduledAt = useMemo(() => {
    if (!scheduledAtStr) return null;
    return parseISO(scheduledAtStr);
  }, [scheduledAtStr]);

  // The instants the create request will carry, and whether the server would
  // refuse the pickup as earlier than the arrival (#4199).
  const requestTimes = useMemo(
    () => requestScheduleTimes({ schedulingType, scheduledAtStr, pickupAtStr }),
    [schedulingType, scheduledAtStr, pickupAtStr],
  );
  const pickupIsBeforeArrival = pickupBeforeScheduled(requestTimes);

  // Default courtesy-car loan window for the work order being created: from
  // drop-off to the requested pickup, opening hours aware (#4094, #4103).
  // Built from the same instants the create request carries.
  const courtesyLoanRange = useMemo(() => {
    if (mode === 'quote' || !requestTimes.scheduledAt) return null;
    return defaultCourtesyLoanRange(
      {
        scheduledAt: requestTimes.scheduledAt,
        estimatedDurationHours: calendarDurationHours > 0 ? calendarDurationHours : null,
        schedulingType,
        vehicleDropoffAt: vehicleDropoffAtStr ? parseISO(vehicleDropoffAtStr).toISOString() : null,
        customerRequestedPickupAt: requestTimes.customerRequestedPickupAt,
      },
      workshopHours,
    );
  }, [mode, requestTimes, calendarDurationHours, schedulingType, vehicleDropoffAtStr, workshopHours]);
  const handleCourtesyVehicleChange = useCallback((next: CreateCourtesySelection | null) => {
    setCourtesyVehicle((prev) => {
      if (prev?.id === next?.id) return prev;
      return next;
    });
    setIsDirty(true);
  }, []);

  // --- Step list ---
  // The new-vehicle form is rendered inline inside the combined customer+vehicle
  // step, so there is no separate 'newVehicle' wizard step.
  // Quotations skip the scheduling step entirely (#848): there is nothing to
  // schedule until the customer accepts the quote and it becomes a work order.
  const stepIds = useMemo(
    (): StepId[] =>
      mode === 'quote'
        ? ['customerVehicle', 'services', 'summary']
        : ['customerVehicle', 'services', 'scheduling', 'summary'],
    [mode],
  );

  // Clamp activeStep should the step list ever shrink.
  useEffect(() => {
    if (activeStep >= stepIds.length) {
      setActiveStep(stepIds.length - 1);
    }
    setMaxStep((m) => Math.min(m, stepIds.length - 1));
  }, [activeStep, stepIds.length]);

  // Buddy guide events (#3897). emitGuideEvent is a no-op while no guide runs,
  // so the wizard behaves the same without one. One report per change: a
  // repeated render must not skip the next guide step.
  const reportedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      reportedStepRef.current = null;
      return;
    }
    const stepId = stepIds[activeStep];
    if (!stepId || reportedStepRef.current === stepId) return;
    reportedStepRef.current = stepId;
    emitGuideEvent('woCreate.stepChanged', { stepId });
  }, [open, activeStep, stepIds]);

  const wasCreatingVehicleRef = useRef(false);
  useEffect(() => {
    if (!open) {
      wasCreatingVehicleRef.current = false;
      return;
    }
    if (isCreatingNewVehicle && !wasCreatingVehicleRef.current) {
      emitGuideEvent('woCreate.newVehicleSelected', { isNewVehicle: true });
    }
    wasCreatingVehicleRef.current = isCreatingNewVehicle;
  }, [open, isCreatingNewVehicle]);

  const lastInlineRef = useRef<InlinePartyCreate>(null);
  useEffect(() => {
    if (!open) {
      lastInlineRef.current = null;
      return;
    }
    if (
      (inlinePartyCreate === 'person' || inlinePartyCreate === 'company')
      && inlinePartyCreate !== lastInlineRef.current
    ) {
      emitGuideEvent('woCreate.newCustomerSelected', {
        isNewCustomer: true,
        customerKind: inlinePartyCreate,
      });
    }
    lastInlineRef.current = inlinePartyCreate;
  }, [open, inlinePartyCreate]);

  const lastResolvedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      lastResolvedRef.current = null;
      return;
    }
    const vehicleReady = resolvedVehicle !== null || isCreatingNewVehicle;
    const customerReady = ordererParty !== null || inlinePartyCreate !== null;
    if (!vehicleReady || !customerReady) {
      lastResolvedRef.current = null;
      return;
    }
    const customerKind = guideCustomerKind(ordererParty, inlinePartyCreate);
    const vehicleKey = isCreatingNewVehicle ? 'new-vehicle' : (resolvedVehicle?.id ?? '');
    const customerKey = inlinePartyCreate
      ?? (ordererParty?.partyType === 'company'
        ? (ordererParty.companyId ?? '')
        : (ordererParty?.personId ?? ''));
    const signature = `${vehicleKey}|${customerKey}|${customerKind}`;
    if (lastResolvedRef.current === signature) return;
    lastResolvedRef.current = signature;
    emitGuideEvent('woCreate.customerVehicleResolved', {
      isNewVehicle: isCreatingNewVehicle,
      isNewCustomer: inlinePartyCreate !== null,
      customerKind,
    });
  }, [open, resolvedVehicle, isCreatingNewVehicle, ordererParty, inlinePartyCreate]);

  const lastServiceCountRef = useRef(0);
  useEffect(() => {
    if (!open) {
      lastServiceCountRef.current = 0;
      return;
    }
    if (selectedServices.length > lastServiceCountRef.current) {
      emitGuideEvent('woCreate.serviceAdded', { serviceCount: selectedServices.length });
    }
    lastServiceCountRef.current = selectedServices.length;
  }, [open, selectedServices]);

  // The default time is already filled in. Report a choice only after it changes,
  // so the schedule step is not skipped before the user has seen it.
  const scheduleBaselineRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      scheduleBaselineRef.current = null;
      return;
    }
    if (scheduleBaselineRef.current === null) {
      scheduleBaselineRef.current = scheduledAtStr;
      return;
    }
    if (scheduledAtStr !== scheduleBaselineRef.current) {
      scheduleBaselineRef.current = scheduledAtStr;
      emitGuideEvent('woCreate.scheduleChosen', { scheduledAt: scheduledAtStr });
    }
  }, [open, scheduledAtStr]);

  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      emitGuideEvent(GUIDE_DIALOG_CLOSED_EVENT);
    }
    wasOpenRef.current = open;
  }, [open]);

  const stepLabels = useMemo(() => {
    const labelMap: Record<StepId, string> = {
      customerVehicle: t('create.stepCustomerVehicle'),
      scheduling: t('create.stepScheduling'),
      services: t('create.stepServices'),
      summary: t('create.stepSummary'),
    };
    return stepIds.map((id) => labelMap[id]);
  }, [stepIds, t]);

  // --- Validation helpers ---

  // Silent draft validation, shared with the Next handler via the commit
  // module (#1020) — canProceed reads this without setting field errors.
  const draftValidation = useMemo(
    () =>
      validateCustomerVehicleDraft({
        inlinePartyCreate,
        personDraft,
        companyDraft,
        isCreatingNewVehicle,
        newVehicleData,
        tPersons,
        tCompanies,
        tVehicles,
      }),
    [inlinePartyCreate, personDraft, companyDraft, isCreatingNewVehicle, newVehicleData, tPersons, tCompanies, tVehicles],
  );

  // canProceed for each step
  const canProceed = useCallback((stepId: StepId): boolean => {
    switch (stepId) {
      case 'customerVehicle': {
        // Vehicle: resolved, or a valid new-vehicle draft (saved on Next, #1020)
        const hasVehicle =
          resolvedVehicle !== null ||
          (isCreatingNewVehicle && Object.keys(draftValidation.vehicleErrors).length === 0);
        if (!hasVehicle) return false;

        // Orderer: a selected party, or a valid inline draft (saved on Next, #1020)
        if (ordererParty !== null) return true;
        if (inlinePartyCreate === 'person') return Object.keys(draftValidation.personErrors).length === 0;
        if (inlinePartyCreate === 'company') return Object.keys(draftValidation.companyErrors).length === 0;
        return false;
      }
      case 'scheduling':
        return scheduledAtStr !== '' && !pickupIsBeforeArrival;
      case 'services':
        return selectedServices.length > 0;
      case 'summary':
        return true;
      default:
        return false;
    }
  }, [
    resolvedVehicle, isCreatingNewVehicle, draftValidation,
    ordererParty, inlinePartyCreate,
    scheduledAtStr, pickupIsBeforeArrival, selectedServices,
  ]);

  // --- Display names ---
  const customerDisplayName = ordererParty?.name ?? '';

  const vehicleRegNumber = useMemo(() => {
    if (resolvedVehicle) return resolvedVehicle.registrationNumber;
    return newVehicleData.registrationNumber.trim();
  }, [resolvedVehicle, newVehicleData]);

  const vehicleDescription = useMemo(() => {
    if (resolvedVehicle) {
      return [resolvedVehicle.make, resolvedVehicle.model, resolvedVehicle.year]
        .filter(Boolean)
        .join(' ');
    }
    return [newVehicleData.make, newVehicleData.model, newVehicleData.year]
      .filter(Boolean)
      .join(' ');
  }, [resolvedVehicle, newVehicleData]);

  const mechanicNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of mechanics) {
      map[m.id] = `${m.firstName} ${m.lastName}`;
    }
    return map;
  }, [mechanics]);

  // Lift names for the summary (#1993)
  const liftNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of allLifts) {
      map[l.id] = `${l.name} (${l.identifier})`;
    }
    return map;
  }, [allLifts]);

  // --- Reset form ---
  const resetForm = useCallback(() => {
    setActiveStep(0);
    setMaxStep(0);
    setSearchInput('');
    setResolvedVehicle(null);
    setIsCreatingNewVehicle(false);
    setNewVehicleData({ ...initialVehicleFieldsData });
    setNewVehicleErrors({});
    setOrdererParty(null);
    setOrdererSource(null);
    setContactPersonRegistryId('');
    setAdditionalParties({});
    setInlinePartyCreate(null);
    setPersonDraft({ ...initialPersonFieldsData });
    setPersonDraftErrors({});
    setCompanyDraft({ ...initialCompanyFieldsData });
    setCompanyDraftErrors({});
    setMileageInput('');
    setPreapprovedLimitInput('');
    setQuoteValidityDaysInput('');
    setQuoteValidityTouched(false);
    setCustomerWaits(false);
    setAllowMinorMaintenance(false);
    setConfirmationMethod(null);
    setSelectedChecklists([]);
    setAdditionalQuestionAnswers({});
    // Clearing the flag lets the seeding effect re-apply the workshop default.
    setSchedulingTypeTouched(false);
    setScheduledAtStr(initialDate ? toDateTimeLocal(initialDate) : getDefaultScheduledAt(workshopHours));
    setVehicleDropoffAtStr('');
    setVehicleDropoffAtTouched(false);
    setPickupAtStr(initialDate ? toDateTimeLocal(initialDate) : getDefaultScheduledAt(workshopHours));
    setPickupAtTouched(false);
    setDurationHours(0);
    setDurationTouched(false);
    setServiceBookChecked(false);
    setSelectedServices([]);
    setCustomerNotesText('');
    setLocalNotes([]);
    setCourtesyVehicle(null);
    setIsDirty(false);
    setShowDiscardConfirm(false);
  }, [initialDate, workshopHours]);

  // Close with dirty check.
  // Also called by MUI Dialog's onClose with a reason — ignore backdrop
  // clicks so an accidental click outside the dialog never discards the
  // in-progress form. The X icon and Cancel button call this without a
  // reason, so they keep closing as before. Esc still closes (dirty check).
  const settleCompanyConflict = useCallback((choice: CompanyBusinessIdChoice) => {
    const resolve = conflictChoiceRef.current;
    conflictChoiceRef.current = null;
    setCompanyConflict(null);
    resolve?.(choice);
  }, []);

  const isWizardLive = useCallback((session: number) => {
    return mountedRef.current && openRef.current && wizardSessionRef.current === session;
  }, []);

  const invalidateWizardSession = useCallback(() => {
    wizardSessionRef.current += 1;
    committingRef.current = false;
    const resolve = conflictChoiceRef.current;
    conflictChoiceRef.current = null;
    resolve?.({ kind: 'cancel' });
    if (!mountedRef.current) return;
    setCompanyConflict(null);
    setIsCommittingStep(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateWizardSession();
    };
  }, [invalidateWizardSession]);

  useEffect(() => {
    if (open) return;
    invalidateWizardSession();
  }, [open, invalidateWizardSession]);

  // Prefill once per open from a vehicle card (#2899). Reset on close so the
  // next open starts clean, then this reapplies if initialVehicle is still set.
  const appliedInitialVehicleRef = useRef(false);
  useEffect(() => {
    if (!open) {
      appliedInitialVehicleRef.current = false;
      return;
    }
    if (appliedInitialVehicleRef.current || !initialVehicle) return;
    appliedInitialVehicleRef.current = true;
    setResolvedVehicle(initialVehicle);
    const party = ordererPartyFromVehicle(initialVehicle);
    if (party) {
      setOrdererParty(party);
      setOrdererSource('vehicle');
      setContactPersonRegistryId('');
    }
    setIsCreatingNewVehicle(false);
  }, [open, initialVehicle]);

  const resolveCompanyBusinessIdConflict = useCallback(
    (matches: readonly BusinessIdMatch[]) =>
      new Promise<CompanyBusinessIdChoice>((resolve) => {
        if (!mountedRef.current || !openRef.current) {
          resolve({ kind: 'cancel' });
          return;
        }
        conflictChoiceRef.current = resolve;
        setCompanyConflict([...matches]);
      }),
    [],
  );

  const closeDialog = useCallback(() => {
    emitGuideEvent(GUIDE_DIALOG_CLOSED_EVENT);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(
    (_event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick') return;
      if (isDirty) {
        setShowDiscardConfirm(true);
      } else {
        invalidateWizardSession();
        resetForm();
        closeDialog();
      }
    },
    [isDirty, resetForm, closeDialog, invalidateWizardSession],
  );

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    formDraft.clear();
    invalidateWizardSession();
    resetForm();
    closeDialog();
  }, [resetForm, closeDialog, invalidateWizardSession, formDraft]);

  // --- Step-1 persistence callbacks (#1020) ---

  // Fired the moment a drafted party is persisted — selecting it as the
  // orderer and clearing the draft makes a retry after a later failure
  // idempotent: the next attempt sees an existing orderer, not a draft.
  const handlePartyCreated = useCallback((party: PartyRef) => {
    setOrdererParty(party);
    setOrdererSource('manual');
    setContactPersonRegistryId('');
    setInlinePartyCreate(null);
    setPersonDraft({ ...initialPersonFieldsData });
    setPersonDraftErrors({});
    setCompanyDraft({ ...initialCompanyFieldsData });
    setCompanyDraftErrors({});
    if (party.partyType === 'person') {
      notify.success(tPersons('saved'), tPersons('savedDesc', { name: party.name }));
    } else {
      notify.success(tCompanies('saved'), tCompanies('savedDesc', { name: party.name }));
    }
  }, [notify, tPersons, tCompanies]);

  // Fired the moment the drafted vehicle is persisted — resolving it in the
  // wizard means neither a retry nor the final submit ever re-creates it.
  const handleVehicleCreatedInStep = useCallback((vehicle: VehicleDetail, owner: PartyRef) => {
    const draftMileage = newVehicleData.mileage.trim();
    setResolvedVehicle({
      id: vehicle.id,
      customerId: vehicle.customerId,
      registrationNumber: vehicle.registrationNumber,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      vin: vehicle.vin,
      mileage: vehicle.mileage,
      lastServiceDate: vehicle.lastServiceDate,
      createdAt: vehicle.createdAt,
      updatedAt: vehicle.updatedAt,
      customerName: owner.name,
      customerPhone: owner.phone ?? '',
      partyType: owner.partyType,
      personId: owner.personId,
      companyId: owner.companyId,
    });
    // Carry the drafted mileage into the booking-level mileage field.
    if (draftMileage && !mileageInput) {
      setMileageInput(draftMileage);
    }
    setIsCreatingNewVehicle(false);
    setNewVehicleData({ ...initialVehicleFieldsData });
    setNewVehicleErrors({});
  }, [newVehicleData, mileageInput]);

  // Saving state for the step-1 Next button — blocks parallel save requests.
  const isSavingCustomerVehicle =
    isCommittingStep ||
    createPersonMutation.isPending ||
    createCompanyMutation.isPending ||
    createVehicleMutation.isPending;

  // --- Navigation ---
  const advanceStep = useCallback(() => {
    setActiveStep((prev) => {
      const next = prev + 1;
      setMaxStep((m) => Math.max(m, next));
      return next;
    });
  }, []);

  const handleNext = useCallback(async () => {
    const sid = stepIds[activeStep];

    // Step 1: validate the drafts with error display, then persist them —
    // party first, then the vehicle linked to it (#1020). The wizard advances
    // only once every needed save has succeeded.
    if (sid === 'customerVehicle') {
      const validation = validateCustomerVehicleDraft({
        inlinePartyCreate,
        personDraft,
        companyDraft,
        isCreatingNewVehicle,
        newVehicleData,
        tPersons,
        tCompanies,
        tVehicles,
      });
      setPersonDraftErrors(validation.personErrors);
      setCompanyDraftErrors(validation.companyErrors);
      setNewVehicleErrors(() => validation.vehicleErrors);
      if (!validation.valid) return;
      if (committingRef.current) return;
      const session = wizardSessionRef.current;
      committingRef.current = true;
      setIsCommittingStep(true);

      // A lookup or earlier save can finish after close. Do not start the next
      // write for a session that is no longer live; a request already in flight
      // is left to finish.
      const startLiveWrite = <T,>(start: () => Promise<T>): Promise<T> => {
        if (!isWizardLive(session)) {
          return Promise.reject(new Error('wizard session closed'));
        }
        return start();
      };

      try {
        const result = await commitCustomerVehicleStep({
          ordererParty,
          inlinePartyCreate,
          personDraft,
          companyDraft,
          isCreatingNewVehicle,
          newVehicleData,
          createPerson: (data) => startLiveWrite(() => createPersonMutation.mutateAsync(data)),
          createCompany: (data) => startLiveWrite(() => createCompanyMutation.mutateAsync(data)),
          createVehicle: (data) => startLiveWrite(() => createVehicleMutation.mutateAsync(data)),
          lookupCompaniesByBusinessId,
          resolveCompanyBusinessIdConflict: (
            matches: readonly BusinessIdMatch[],
          ): Promise<CompanyBusinessIdChoice> => {
            if (!isWizardLive(session)) return Promise.resolve({ kind: 'cancel' });
            return resolveCompanyBusinessIdConflict(matches);
          },
          onPartyCreated: (party: PartyRef) => {
            if (!isWizardLive(session)) return;
            handlePartyCreated(party);
          },
          onVehicleCreated: (vehicle: VehicleDetail, owner: PartyRef) => {
            if (!isWizardLive(session)) return;
            handleVehicleCreatedInStep(vehicle, owner);
          },
        });
        if (!isWizardLive(session)) return;
        if (!result.ok) {
          if (!('error' in result)) return;
          if (result.phase === 'company' && result.error instanceof ApiError && result.error.status === 409) {
            setCompanyDraftErrors({ businessId: tCompanies('businessIdExists') });
          } else if (result.phase === 'person') {
            notify.error(tc('error'), describeApiError(result.error, tc, { fallback: tPersons('saveFailed') }));
          } else if (result.phase === 'company') {
            notify.error(tc('error'), describeApiError(result.error, tc, { fallback: tCompanies('saveFailed') }));
          } else {
            notify.error(tc('error'), describeApiError(result.error, tc, { fallback: tVehicles('form.saveFailed') }));
          }
          return;
        }
      } finally {
        if (wizardSessionRef.current === session) {
          committingRef.current = false;
          if (mountedRef.current) setIsCommittingStep(false);
        }
      }

      // The plate already has an open quote (#2653): ask before creating a
      // second work order for the same job. Only for a vehicle that existed
      // before this wizard and only when creating a work order, not a quote.
      if (mode === 'workOrder' && !isCreatingNewVehicle && resolvedVehicle) {
        const quote = await findOpenQuoteForVehicle(resolvedVehicle.id);
        if (!isWizardLive(session)) return;
        if (quote) {
          // Offer the choice before the wizard step changes. The synthetic
          // step id is not a wizard step, so the later real advance still reports.
          emitGuideEvent('woCreate.openQuoteOffered', {
            openQuoteOffered: true,
            quoteId: quote.id,
          });
          emitGuideEvent('woCreate.stepChanged', { stepId: 'openQuote' });
          setOpenQuoteCandidate(quote);
          return;
        }
      }
    }

    advanceStep();
  }, [
    activeStep, stepIds, mode, resolvedVehicle, advanceStep,
    inlinePartyCreate, personDraft, companyDraft, isCreatingNewVehicle, newVehicleData,
    ordererParty, tPersons, tCompanies, tVehicles, tc, notify,
    createPersonMutation, createCompanyMutation, createVehicleMutation,
    handlePartyCreated, handleVehicleCreatedInStep,
    resolveCompanyBusinessIdConflict, isWizardLive,
  ]);

  // The user chose the open quote: accept and schedule it instead of creating.
  const handleUseOpenQuote = useCallback(() => {
    if (!openQuoteCandidate) return;
    setAcceptingQuoteId(openQuoteCandidate.id);
    setOpenQuoteCandidate(null);
  }, [openQuoteCandidate]);

  // The user wants a fresh work order after all: carry on as before.
  const handleDeclineOpenQuote = useCallback(() => {
    setOpenQuoteCandidate(null);
    advanceStep();
  }, [advanceStep]);

  const handleOpenQuoteAccepted = useCallback((workOrderId: string) => {
    setAcceptingQuoteId(null);
    formDraft.clear();
    invalidateWizardSession();
    resetForm();
    // Navigate BEFORE closing, as handleSubmit does, so the unmount cannot
    // race the navigation.
    closeDialog();
    navigate(`/workorders/${workOrderId}`);
  }, [invalidateWizardSession, resetForm, closeDialog, navigate, formDraft]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    if (!scheduledAt) return;
    // Refuse before anything is written (the vehicle is created first): the
    // server would reject the work order and the toast could only guess (#4199).
    if (pickupIsBeforeArrival) {
      notify.error(
        mode === 'quote' ? t('quotationCreateFailed') : t('workOrderCreateFailed'),
        t('create.pickupBeforeArrivalError'),
      );
      return;
    }

    let attempt: SubmitAttempt | null = null;
    try {
      // 1. The orderer is a person/company party from the registries (#833 B1).
      if (!ordererParty) {
        throw new Error('No orderer selected');
      }
      const ordererRef = {
        partyType: ordererParty.partyType,
        personId: ordererParty.partyType === 'person' ? ordererParty.personId : undefined,
        companyId: ordererParty.partyType === 'company' ? ordererParty.companyId : undefined,
      };

      // 2. Create vehicle if new — owned by the orderer party.
      let vehicleId: string;
      if (isCreatingNewVehicle) {
        const created = await createVehicleMutation.mutateAsync({
          owner: ordererRef,
          registrationNumber: newVehicleData.registrationNumber.trim(),
          make: newVehicleData.make.trim() || undefined,
          model: newVehicleData.model.trim() || undefined,
          year: newVehicleData.year ? Number(newVehicleData.year) : undefined,
          vin: newVehicleData.vin.trim() || undefined,
          mileage: newVehicleData.mileage ? Number(newVehicleData.mileage) : undefined,
        });
        vehicleId = created.id;
      } else {
        vehicleId = resolvedVehicle!.id;
      }

      // 3. Build service IDs, the parallel mechanics/lifts arrays and duration.
      // Arrays (not records) are used so the same serviceId can appear more
      // than once. Every entry holds the SAME mechanic and the SAME lift: one
      // work order goes to one mechanic on one lift (#1984/#1993), and usually
      // to neither — planning assigns them, so an all-empty array is omitted.
      const serviceIds = selectedServices.map((s) => s.serviceId);
      const serviceMechanics: (string | null)[] = selectedServices.map((s) => s.mechanicId || null);
      const serviceLifts: (string | null)[] = selectedServices.map((s) => s.liftId || null);
      const hasAnyMechanic = serviceMechanics.some((m) => m !== null);
      const hasAnyLift = serviceLifts.some((l) => l !== null);
      // Per-service durations parallel to serviceIds (#2667). Non-null entries
      // latch planned_hours_manual; catalog defaults stay null so labor-hour
      // updates can still derive them. Omit the field when every row is derived.
      const serviceDurations = toCreateServiceDurations(selectedServices);
      // Ticked optional steps per row (#3347); omitted when no row has any.
      const serviceLaborItemIds = toCreateServiceLaborItemIds(selectedServices);
      const estimatedDurationHours = toCreateEstimatedDurationHours(serviceIds, durationHours);
      // Use first assigned mechanic as booking-level mechanic (backward compat)
      const primaryMechanicId = selectedServices.find((s) => s.mechanicId)?.mechanicId;

      // 4. Parse pre-approved limit from euros to cents
      const preapprovedLimitCents = preapprovedLimitInput
        ? eurosToCents(preapprovedLimitInput)
        : undefined;

      const answeredAdditionalQuestions = additionalQuestions
        .filter((q) => q.isActive)
        .flatMap((q) => {
          const answer = (additionalQuestionAnswers[q.id] ?? '').trim();
          if (answer === '') return [];
          return [{ questionId: q.id, label: q.label, answer }];
        });

      // 5. The whole work order goes in one request (#3160). Parties, notes,
      // comments, the service-book check and the chosen lists are written by the
      // same create transaction, so one failure rolls the whole work order back.
      const serviceComments: [string, string][] = [];
      for (const service of selectedServices) {
        const text = (service.notes ?? '').trim();
        if (text) serviceComments.push([service.rowId, text]);
      }
      const extraParties: NonNullable<WizardBooking['additionalParties']> = {};
      for (const [role, party] of Object.entries(additionalParties) as [WorkOrderPartyRole, PartyRef][]) {
        // The orderer and the primary contact have their own request fields.
        if (role === 'orderer' || role === 'primary_contact') continue;
        extraParties[role] = {
          partyType: party.partyType,
          personId: party.partyType === 'person' ? (party.personId ?? undefined) : undefined,
          companyId: party.partyType === 'company' ? (party.companyId ?? undefined) : undefined,
        };
      }
      const workNotes = localNotes.flatMap((note) => {
        const content = note.content.trim();
        return content ? [{ category: note.category, content }] : [];
      });

      const body: WizardBooking = {
        orderer: ordererRef,
        primaryContactPersonId: contactPersonRegistryId || undefined,
        vehicleId,
        initialStatus: mode === 'quote' ? 'quote' : undefined,
        quoteValidityDays:
          mode === 'quote' && quoteValidityDaysInput.trim()
            ? Number(quoteValidityDaysInput)
            : undefined,
        // A floating booking carries only the day: UTC midnight of that civil
        // date (#1965). Same encoding as WorkOrderScheduleBar.
        scheduledAt: requestTimes.scheduledAt ?? scheduledAt.toISOString(),
        schedulingType,
        vehicleDropoffAt: vehicleDropoffAtStr ? parseISO(vehicleDropoffAtStr).toISOString() : null,
        // The pickup default is the duration+buffer estimate (#2117), from the
        // opening time for floating work (#4095). A floating pickup equal to
        // the midnight day marker (e.g. an older draft) is not a real pickup.
        customerRequestedPickupAt: requestTimes.customerRequestedPickupAt,
        serviceIds,
        serviceTitles: selectedServices.map((s) => s.title?.trim() || s.serviceName),
        serviceMechanics: hasAnyMechanic ? serviceMechanics : undefined,
        serviceLifts: hasAnyLift ? serviceLifts : undefined,
        ...(serviceDurations !== undefined ? { serviceDurations } : {}),
        ...(serviceLaborItemIds !== undefined ? { serviceLaborItemIds } : {}),
        ...(estimatedDurationHours !== undefined ? { estimatedDurationHours } : {}),
        assignedMechanicId: primaryMechanicId || undefined,
        mileage: mileageInput ? Number(mileageInput) : undefined,
        customerWaits: customerWaits || undefined,
        allowMinorMaintenance: allowMinorMaintenance || undefined,
        jointInspectionRequested: jointInspectionRequested || undefined,
        customerNotes: customerNotesText.trim() || undefined,
        preapprovedAdditionalWorkLimitCents: preapprovedLimitCents,
        // Create-time confirmation (#2126) and reception answers (#2130) are
        // part of the shared work-order create contract (#3178).
        ...(confirmationMethod !== null ? { confirmationMethod } : {}),
        ...(answeredAdditionalQuestions.length > 0
          ? { additionalQuestionAnswers: answeredAdditionalQuestions }
          : {}),
        // Row ids tie each service row to its comment and to its place in the result (#3160).
        serviceRowIds: selectedServices.map((service) => service.rowId),
        ...(serviceComments.length > 0 ? { serviceNotes: Object.fromEntries(serviceComments) } : {}),
        ...(Object.keys(extraParties).length > 0 ? { additionalParties: extraParties } : {}),
        ...(serviceBookChecked ? { serviceBookChecked: true } : {}),
        ...(workNotes.length > 0 ? { notes: workNotes } : {}),
        ...(selectedChecklists.length > 0
          ? { checklistSelections: selectedChecklists.map(({ kind, templateId }) => ({ kind, templateId })) }
          : {}),
      };

      // 6. One logical submit, one Idempotency-Key (#3160). A lost response and
      // a button press after it send the same key and get the first outcome back.
      const fingerprint = JSON.stringify(body);
      attempt = {
        key: nextSubmitKey(submitAttemptRef.current, fingerprint),
        fingerprint,
        refusedBeforeWrite: false,
      };
      submitAttemptRef.current = attempt;
      const newBooking = await createBooking.mutateAsync({ body, idempotencyKey: attempt.key });
      submitAttemptRef.current = null;

      // 7. A rejected value fails the create above, so only the optional primary
      // contact can be missing here. Say so: the work order exists and the
      // contact can be added on it (#3160).
      if (newBooking.unsavedOptionalParts.length > 0) {
        notify.warning(
          t('create.optionalPartNotSavedTitle'),
          t('create.optionalPartNotSavedDesc', {
            parts: newBooking.unsavedOptionalParts.map((part) => t(`parties.roles.${part}`)).join(', '),
          }),
        );
      }

      notify.success(
        mode === 'quote' ? t('quotationCreated') : t('workOrderCreated'),
        `${customerDisplayName} - ${format(scheduledAt, 'd.M.yyyy HH:mm')}`
      );
      // 8. The courtesy car picked in the wizard (#4103). The work order is
      // already saved: a refused reservation leaves it as it is and points the
      // user to the courtesy-car card instead of failing the create.
      if (courtesyVehicle && mode !== 'quote') {
        const outcome = await reserveCourtesyAfterCreate({
          courtesyVehicleId: courtesyVehicle.id,
          workOrderId: newBooking.id,
          range: courtesyLoanRange,
          reserve: (data) => reserveCourtesyLoan.mutateAsync(data),
        });
        if (outcome === 'reserved') {
          notify.success(t('courtesy.reserved'), t('courtesy.reservedDesc', { plate: courtesyVehicle.label }));
        } else if (outcome !== 'none') {
          notify.warning(
            t('courtesy.createReserveFailedTitle'),
            t(
              outcome === 'unavailable'
                ? 'courtesy.createReserveUnavailableDesc'
                : 'courtesy.createReserveFailedDesc',
              { plate: courtesyVehicle.label },
            ),
          );
        }
      }

      formDraft.clear();
      resetForm();

      // Created before the dialog-closed event: closing first would cancel the
      // guide instead of finishing it (#3897).
      const targetId = newBooking.id;
      emitGuideEvent('woCreate.created', { id: targetId, mode });
      // Navigate BEFORE closing modal to avoid unmount race condition
      // (onClose triggers re-render that could unmount this component before navigate fires)
      closeDialog();
      navigate(`/workorders/${targetId}`, { state: { openSection: 'services' } });
    } catch (error) {
      emitGuideEvent('woCreate.createFailed');
      // Keep the attempt's key for the next press unless the server proved it
      // wrote nothing (#3160). The key and body are kept as they were sent.
      if (attempt) submitAttemptRef.current = { ...attempt, refusedBeforeWrite: refusedBeforeWrite(error) };
      notify.error(
        mode === 'quote' ? t('quotationCreateFailed') : t('workOrderCreateFailed'),
        outcomeMayBeUnknown(error)
          ? t('create.submitOutcomeUnknown')
          : isPickupBeforeScheduledRefusal(error)
          ? t('create.pickupBeforeArrivalError')
          : describeApiError(error, tc, {
              fallback: t('create.unknownError'),
              codePrefix: 'workorders:create.followupRejected',
            })
      );
    }
  }, [
    mode,
    scheduledAt, schedulingType, vehicleDropoffAtStr, isCreatingNewVehicle,
    requestTimes, pickupIsBeforeArrival,
    ordererParty, contactPersonRegistryId, resolvedVehicle, newVehicleData,
    additionalParties,
    selectedServices, durationHours, serviceBookChecked, selectedChecklists, localNotes, mileageInput, customerWaits,
    allowMinorMaintenance, jointInspectionRequested, confirmationMethod, customerNotesText, preapprovedLimitInput,
    additionalQuestions, additionalQuestionAnswers,
    quoteValidityDaysInput,
    createVehicleMutation,
    createBooking,
    courtesyVehicle, courtesyLoanRange, reserveCourtesyLoan,
    notify, navigate, t, tc, customerDisplayName,
    resetForm, closeDialog, formDraft,
  ]);

  const isSubmitting =
    createBooking.isPending ||
    createVehicleMutation.isPending ||
    reserveCourtesyLoan.isPending;

  const activeStepId = stepIds[activeStep];
  const footerButtons = getCreateWizardFooterButtons({
    activeStepIndex: activeStep,
    stepCount: stepIds.length,
    canProceed: activeStepId !== undefined && canProceed(activeStepId),
    isSavingCustomerVehicle,
    isSubmitting,
  });

  // Concise summary shown on a completed/collapsed step so earlier input stays
  // visible in the inline flow (#561). Service names are domain data (not
  // localised). Empty string → no summary caption (e.g. not-yet-filled steps).
  const stepSummary = useCallback((sid: StepId): string => {
    switch (sid) {
      case 'customerVehicle':
        return [vehicleRegNumber, customerDisplayName].filter(Boolean).join(' · ');
      case 'services':
        return selectedServices.map((s) => s.serviceName).filter(Boolean).join(', ');
      case 'scheduling':
        return [
          scheduledAt ? format(scheduledAt, 'd.M. HH:mm') : '',
          [...new Set(selectedServices.map((s) => s.mechanicId).filter(Boolean))]
            .map((id) => mechanicNames[id])
            .filter(Boolean)
            .join(', '),
        ].filter(Boolean).join(' · ');
      case 'summary':
        return '';
    }
  }, [vehicleRegNumber, customerDisplayName, selectedServices, scheduledAt, mechanicNames]);

  // --- Render step content ---
  const renderStepContent = (sid: StepId) => {
    switch (sid) {
      case 'customerVehicle':
        return (
          <CustomerVehicleStep
            // Unified search input (#1002)
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            // Vehicle props
            resolvedVehicle={resolvedVehicle}
            setResolvedVehicle={setResolvedVehicle}
            isCreatingNewVehicle={isCreatingNewVehicle}
            setIsCreatingNewVehicle={setIsCreatingNewVehicle}
            mileageInput={mileageInput}
            setMileageInput={setMileageInput}
            // New vehicle props
            newVehicleData={newVehicleData}
            setNewVehicleData={setNewVehicleData}
            newVehicleErrors={newVehicleErrors}
            setNewVehicleErrors={setNewVehicleErrors}
            // Orderer props (#833 B1)
            ordererParty={ordererParty}
            setOrdererParty={setOrdererParty}
            ordererSource={ordererSource}
            setOrdererSource={setOrdererSource}
            contactPersonRegistryId={contactPersonRegistryId}
            setContactPersonRegistryId={setContactPersonRegistryId}
            // Inline person/company creation (#1002)
            inlinePartyCreate={inlinePartyCreate}
            setInlinePartyCreate={setInlinePartyCreate}
            personDraft={personDraft}
            setPersonDraft={setPersonDraft}
            personErrors={personDraftErrors}
            setPersonErrors={setPersonDraftErrors}
            companyDraft={companyDraft}
            setCompanyDraft={setCompanyDraft}
            companyErrors={companyDraftErrors}
            setCompanyErrors={setCompanyDraftErrors}
            // Additional party roles (#1108)
            additionalParties={additionalParties}
            onAddParty={handleAddParty}
            onRemoveParty={handleRemoveParty}
            // Common props
            setIsDirty={setIsDirty}
          />
        );
      case 'scheduling':
        return (
          <>
          <SchedulingStep
            scheduledAtStr={scheduledAtStr}
            setScheduledAtStr={setScheduledAtStr}
            vehicleDropoffAtStr={vehicleDropoffAtStr}
            setVehicleDropoffAtStr={(v) => {
              setVehicleDropoffAtStr(v);
              setVehicleDropoffAtTouched(true);
            }}
            schedulingType={schedulingType}
            setSchedulingType={setSchedulingType}
            schedulingTypeTouched={schedulingTypeTouched}
            defaultSchedulingType={defaultSchedulingType}
            {...(workshopHours ? { businessHours: workshopHours } : {})}
            onSchedulingTypeUserChange={() => setSchedulingTypeTouched(true)}
            pickupAtStr={pickupAtStr}
            setPickupAtStr={(v) => {
              setPickupAtStr(v);
              setPickupAtTouched(true);
            }}
            pickupBeforeArrival={pickupIsBeforeArrival}
            estimatedDurationHours={calendarDurationHours}
            {...(selectedServices.length === 0
              ? {
                  setEstimatedDurationHours: (v: number) => {
                    setDurationHours(v);
                    setDurationTouched(true);
                  },
                }
              : {})}
            pickupBufferMinutes={defaultPickupBufferMinutes}
            durationStepMinutes={workDurationStepMinutes}
            mechanicIds={[...new Set(selectedServices.map((s) => s.mechanicId).filter(Boolean))]}
            selectedServices={selectedServices}
            customerWaits={customerWaits}
            setCustomerWaits={setCustomerWaits}
            allowMinorMaintenance={allowMinorMaintenance}
            setAllowMinorMaintenance={setAllowMinorMaintenance}
            preapprovedLimitInput={preapprovedLimitInput}
            setPreapprovedLimitInput={setPreapprovedLimitInput}
            jointInspectionRequested={jointInspectionRequested}
            setJointInspectionRequested={setJointInspectionRequested}
            setIsDirty={setIsDirty}
          />
          <CourtesyVehicleSelect
            plannedStart={courtesyLoanRange?.plannedStart ?? null}
            plannedEnd={courtesyLoanRange?.plannedEnd ?? null}
            value={courtesyVehicle}
            onChange={handleCourtesyVehicleChange}
          />
          </>
        );
      case 'services':
        return (
          <ServiceStep
            selectedServices={selectedServices}
            setSelectedServices={setSelectedServices}
            setIsDirty={setIsDirty}
            defaultMechanicId={initialMechanicId}
            durationStepMinutes={workDurationStepMinutes}
          />
        );
      case 'summary':
        return (
          <SummaryStep
            serviceBookChecked={serviceBookChecked}
            onServiceBookCheckedChange={(v) => {
              setServiceBookChecked(v);
              setIsDirty(true);
            }}
            mode={mode}
            schedulingType={schedulingType}
            vehicleRegNumber={vehicleRegNumber}
            vehicleDescription={vehicleDescription}
            customerDisplayName={customerDisplayName}
            isCreatingNewVehicle={isCreatingNewVehicle}
            isCreatingNewCustomer={false}
            scheduledAtStr={scheduledAtStr}
            pickupAtStr={pickupAtStr}
            vehicleDropoffAtStr={vehicleDropoffAtStr}
            courtesyVehicleLabel={mode === 'quote' ? null : (courtesyVehicle?.label ?? null)}
            customerWaits={customerWaits}
            allowMinorMaintenance={allowMinorMaintenance}
            jointInspectionRequested={jointInspectionRequested}
            selectedServices={selectedServices}
            mechanicNames={mechanicNames}
            liftNames={liftNames}
            localNotes={localNotes}
            showManagerNotes={showManagerNotes}
            preapprovedLimitEuros={preapprovedLimitInput ? parseFloat(preapprovedLimitInput) : null}
            quoteValidityDaysInput={quoteValidityDaysInput}
            onQuoteValidityDaysChange={(v) => {
              setQuoteValidityDaysInput(v);
              setQuoteValidityTouched(true);
              setIsDirty(true);
            }}
            confirmationMethod={confirmationMethod}
            onConfirmationMethodChange={(v) => {
              setConfirmationMethod(v);
              setIsDirty(true);
            }}
            selectedChecklists={selectedChecklists}
            onSelectedChecklistsChange={(v) => {
              setSelectedChecklists(v);
              setIsDirty(true);
            }}
            additionalQuestions={additionalQuestions}
            additionalQuestionAnswers={additionalQuestionAnswers}
            onAdditionalQuestionAnswerChange={(questionId, answer) => {
              setAdditionalQuestionAnswers((prev) => ({ ...prev, [questionId]: answer }));
              setIsDirty(true);
            }}
          />
        );
    }
  };

  const startCreateResize = (
    event: ReactPointerEvent<HTMLElement>,
    cursor: string,
    next: (displayed: WorkOrderCreateLayout, stored: WorkOrderCreateLayout, dx: number, dy: number) => WorkOrderCreateLayout,
  ) => {
    const displayed = createLayoutRef.current;
    const stored = storedLayoutRef.current;
    endCreateResize.current?.();
    endCreateResize.current = trackPointerResize(event, cursor, (dx, dy) => {
      updateLayout(next(displayed, stored, dx, dy));
    });
  };

  const resizeDialog = (
    factor: { x: number; y: number; cursor: string },
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    startCreateResize(event, factor.cursor, (displayed, stored, dx, dy) => ({
      ...stored,
      dialogWidth: displayed.dialogWidth + dx * factor.x,
      dialogHeight: displayed.dialogHeight + dy * factor.y,
    }));
  };

  const panelBounds = sidePanelBounds(
    createLayout.dialogWidth,
    showHistoryPanel ? createLayout.historyPanelWidth : 0,
  );
  const historyPanelBounds = sidePanelBounds(createLayout.dialogWidth, createLayout.sidePanelWidth);

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      mobileTitle={mode === 'quote' ? t('newQuotation') : t('newWorkOrder')}
      slotProps={{
        paper: {
          sx: {
            position: 'relative',
            ...(!isMobile && {
              width: createLayout.dialogWidth,
              height: createLayout.dialogHeight,
              maxWidth: 'calc(100vw - 64px)',
              maxHeight: 'calc(100vh - 64px)',
              // Keep the edge handles on the visible frame. The body scrolls.
              overflow: 'hidden',
            }),
          },
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{mode === 'quote' ? t('newQuotation') : t('newWorkOrder')}</Typography>
          <Tooltip title={tc('close')}>
            <IconButton onClick={handleClose} size="small" aria-label={tc('close')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          ...(!isMobile && { flex: 1, minHeight: 0 }),
        }}
      >
        {showDraftBanner && restorableDraft ? (
          <Alert
            severity="info"
            data-testid="create-draft-banner"
            sx={{ mb: 2, flexShrink: 0 }}
            action={(
              <>
                <Button color="inherit" size="small" onClick={restoreDraft}>
                  {t('create.draftRestore')}
                </Button>
                <Button color="inherit" size="small" onClick={discardDraft}>
                  {t('create.draftDiscard')}
                </Button>
              </>
            )}
          >
            {t('create.draftFound', { time: format(new Date(restorableDraft.savedAt), 'd.M.yyyy HH:mm') })}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left: notes — desktop only */}
          {!isMobile && (
            <Box
              data-testid="create-wizard-side-panel"
              sx={{
                width: createLayout.sidePanelWidth,
                flexShrink: 0,
                pr: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <WorkOrderNotesPreview
                bookingId=""
                creationMode
                vertical
                localNotes={localNotes}
                onLocalNoteAdd={handleLocalNoteAdd}
                onOpenDialog={() => setNotesDialogOpen(true)}
                customerNotes={customerNotesText}
                onCustomerNotesChange={(v) => { setCustomerNotesText(v); setIsDirty(true); }}
              />
            </Box>
          )}
          {!isMobile && (
            <WorkOrderCreateSidePanelResize
              label={t('create.resizeSidePanel')}
              valueText={t('create.resizeSidePanelValue', { width: createLayout.sidePanelWidth })}
              valueNow={createLayout.sidePanelWidth}
              valueMin={panelBounds.min}
              valueMax={panelBounds.max}
              onPointerDown={(event) => {
                startCreateResize(event, 'col-resize', (displayed, stored, dx) => ({
                  ...stored,
                  sidePanelWidth: displayed.sidePanelWidth + dx,
                }));
              }}
              onKeyDown={(event) => {
                const displayed = createLayoutRef.current;
                const bounds = sidePanelBounds(
                  displayed.dialogWidth,
                  showHistoryPanel ? displayed.historyPanelWidth : 0,
                );
                const current = displayed.sidePanelWidth;
                let next: number | null = null;
                if (event.key === 'ArrowRight') next = current + SIDE_PANEL_KEYBOARD_STEP;
                else if (event.key === 'ArrowLeft') next = current - SIDE_PANEL_KEYBOARD_STEP;
                else if (event.key === 'Home') next = bounds.min;
                else if (event.key === 'End') next = bounds.max;
                if (next === null) return;
                event.preventDefault();
                updateLayout({ ...storedLayoutRef.current, sidePanelWidth: next });
              }}
            />
          )}

          {/* Center: wizard stepper + step content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {/* History stays in view on every step; its side panel is desktop-only. */}
            {isMobile && historyVehicleId && (
              <Box
                data-testid="create-wizard-history-mobile"
                sx={{ mb: 2, flexShrink: 0, maxHeight: 220, overflow: 'auto', overscrollBehavior: 'contain' }}
              >
                <VehicleHistorySummary vehicleId={historyVehicleId} />
              </Box>
            )}
            {/* Notes button — mobile only */}
            {isMobile && (
              <Box sx={{ mb: 2, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<NotesIcon />}
                  onClick={() => setNotesDialogOpen(true)}
                  size="small"
                >
                  {t('create.stepNotes')}
                  {(localNotes.length > 0 || customerNotesText.trim()) && ` (${String(localNotes.length + (customerNotesText.trim() ? 1 : 0))})`}
                </Button>
              </Box>
            )}

            {/* Wizard steps — progressive vertical reveal (#561): each section
                opens below the previous one; completed sections collapse to an
                editable summary so earlier input stays visible. */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, pr: 1 }}>
              <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
                {stepIds.map((sid, index) => {
                  const summary = stepSummary(sid);
                  const header = getCreateWizardStepHeader({
                    index,
                    activeStepIndex: activeStep,
                    maxStepIndex: maxStep,
                    nextDisabled:
                      footerButtons.primaryAction !== 'next' || footerButtons.primaryDisabled,
                  });
                  return (
                    <Step key={sid} completed={index < maxStep}>
                      <StepButton
                        onClick={() => {
                          if (header.disabled) return;
                          if (header.action === 'next') void handleNext();
                          else if (header.action === 'jump') setActiveStep(index);
                        }}
                        disabled={header.disabled}
                        optional={
                          index !== activeStep && summary ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: 'block', maxWidth: 360 }}
                            >
                              {summary}
                            </Typography>
                          ) : undefined
                        }
                      >
                        {stepLabels[index]}
                      </StepButton>
                      <StepContent TransitionProps={{ unmountOnExit: true }}>
                        <Box sx={{ pt: 1 }}>{renderStepContent(sid)}</Box>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </Box>

          {/* Right: AI service-history summary — desktop only */}
          {showHistoryPanel && (
            <WorkOrderCreateSidePanelResize
              label={t('create.resizeHistoryPanel')}
              valueText={t('create.resizeHistoryPanelValue', { width: createLayout.historyPanelWidth })}
              valueNow={createLayout.historyPanelWidth}
              valueMin={historyPanelBounds.min}
              valueMax={historyPanelBounds.max}
              testId="create-wizard-history-panel-resize"
              onPointerDown={(event) => {
                // The handle sits on the panel's left edge: moving left widens it.
                startCreateResize(event, 'col-resize', (displayed, stored, dx) => ({
                  ...stored,
                  historyPanelWidth: displayed.historyPanelWidth - dx,
                }));
              }}
              onKeyDown={(event) => {
                const displayed = createLayoutRef.current;
                const bounds = sidePanelBounds(displayed.dialogWidth, displayed.sidePanelWidth);
                const current = displayed.historyPanelWidth;
                let next: number | null = null;
                if (event.key === 'ArrowLeft') next = current + SIDE_PANEL_KEYBOARD_STEP;
                else if (event.key === 'ArrowRight') next = current - SIDE_PANEL_KEYBOARD_STEP;
                else if (event.key === 'Home') next = bounds.min;
                else if (event.key === 'End') next = bounds.max;
                if (next === null) return;
                event.preventDefault();
                updateLayout({ ...storedLayoutRef.current, historyPanelWidth: next });
              }}
            />
          )}
          {!isMobile && historyVehicleId && (
            <Box
              data-testid="create-wizard-history-panel"
              sx={{
                width: createLayout.historyPanelWidth,
                flexShrink: 0,
                pl: 1,
                minHeight: 0,
                overflow: 'auto',
                overscrollBehavior: 'contain',
              }}
            >
              <VehicleHistorySummary vehicleId={historyVehicleId} />
            </Box>
          )}
        </Box>

        <WorkOrderNotesDialog
          open={notesDialogOpen}
          onClose={() => setNotesDialogOpen(false)}
          bookingId=""
          creationMode
          localNotes={localNotes}
          onLocalNoteAdd={handleLocalNoteAdd}
          customerNotes={customerNotesText}
          onCustomerNotesChange={(v) => { setCustomerNotesText(v); setIsDirty(true); }}
        />
      </DialogContent>

      <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, flexWrap: 'wrap', gap: 1, rowGap: 1, flexShrink: 0 }}>
        {/* Compact summary — hidden on mobile so Cancel + Previous + Submit
            can wrap instead of overflowing (~400 px, #2112 follow-up). */}
        {!isMobile && (vehicleRegNumber || customerDisplayName) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mr: 'auto', minWidth: 0, overflow: 'hidden', maxWidth: '50%' }}
            noWrap
          >
            {[vehicleRegNumber, customerDisplayName, scheduledAt ? format(scheduledAt, 'd.M. HH:mm') : '']
              .filter(Boolean)
              .join(' · ')}
            {selectedServices.length > 0 && ` · ${selectedServices.length}`}
          </Typography>
        )}

        <Button onClick={handleClose} color="inherit" sx={{ flexShrink: 0 }}>
          {tc('cancel')}
        </Button>
        {footerButtons.showPrevious && (
          <Button onClick={handleBack} color="inherit" sx={{ flexShrink: 0 }}>
            {tc('previous')}
          </Button>
        )}
        {footerButtons.primaryAction === 'next' ? (
          <Button
            variant="contained"
            onClick={() => void handleNext()}
            disabled={footerButtons.primaryDisabled}
            sx={{ flexShrink: 0 }}
            {...guideAnchor('wo-create.next')}
          >
            {footerButtons.primaryLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {tc('next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={footerButtons.primaryDisabled}
            sx={{ flexShrink: 0 }}
            {...guideAnchor('wo-create.submit')}
          >
            {footerButtons.primaryLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {mode === 'quote' ? t('create.submitQuotation') : t('create.submit')}
          </Button>
        )}
      </DialogActions>

      <BusinessIdConflictDialog
        open={companyConflict != null}
        mode="create"
        businessId={companyDraft.businessId.trim()}
        matches={companyConflict ?? []}
        onUseExisting={(existing) => {
          settleCompanyConflict({ kind: 'useExisting', company: existing });
        }}
        onConfirmBranch={() => {
          settleCompanyConflict({ kind: 'createBranch' });
        }}
        onCancel={() => {
          settleCompanyConflict({ kind: 'cancel' });
        }}
        isSaving={createCompanyMutation.isPending || createVehicleMutation.isPending}
      />
      <ConfirmDialog
        open={showDiscardConfirm}
        title={tc('unsavedChanges')}
        message={tc('unsavedChangesMessage')}
        confirmText={tc('discard')}
        confirmColor="error"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      {/* The confirm dialog is portaled, so the guide anchor stays in this dialog (#3897). */}
      {openQuoteCandidate !== null && (
        <Box
          {...guideAnchor('wo-create.openQuote')}
          aria-hidden
          sx={{ position: 'absolute', width: 8, height: 8, overflow: 'hidden' }}
        />
      )}
      {/* Open quote on this plate (#2653) */}
      <ConfirmDialog
        open={openQuoteCandidate !== null}
        title={t('create.openQuoteTitle')}
        message={t('create.openQuoteMessage', {
          registration: openQuoteCandidate?.vehicleRegNumber ?? '',
          number: openQuoteCandidate?.workOrderNumber ?? '',
          services: openQuoteCandidate?.serviceType ?? '',
        })}
        confirmText={t('create.useOpenQuote')}
        cancelText={t('create.createNewInstead')}
        onConfirm={handleUseOpenQuote}
        onCancel={handleDeclineOpenQuote}
      />
      {acceptingQuoteId !== null && (
        <QuoteAcceptFlow
          quoteId={acceptingQuoteId}
          initialScheduledAt={initialDate}
          initialMechanicId={initialMechanicId}
          onClose={() => { setAcceptingQuoteId(null); }}
          onAccepted={handleOpenQuoteAccepted}
        />
      )}
      {!isMobile && (
        <WorkOrderCreateDialogResize
          label={t('create.resizeDialog')}
          valueText={t('create.resizeDialogValue', {
            width: createLayout.dialogWidth,
            height: createLayout.dialogHeight,
          })}
          onEdgePointerDown={resizeDialog}
          onKeyDown={(event) => {
            const current = createLayoutRef.current;
            let widthDelta = 0;
            let heightDelta = 0;
            if (event.key === 'ArrowRight') widthDelta = DIALOG_KEYBOARD_STEP;
            else if (event.key === 'ArrowLeft') widthDelta = -DIALOG_KEYBOARD_STEP;
            else if (event.key === 'ArrowDown') heightDelta = DIALOG_KEYBOARD_STEP;
            else if (event.key === 'ArrowUp') heightDelta = -DIALOG_KEYBOARD_STEP;
            else return;
            event.preventDefault();
            updateLayout({
              ...storedLayoutRef.current,
              dialogWidth: current.dialogWidth + widthDelta,
              dialogHeight: current.dialogHeight + heightDelta,
            });
          }}
        />
      )}
    </ResponsiveDialog>
  );
}
